// Team leaderboard (spec §11.5.9). Cohort-only — global leaderboard
// deferred. Top-3 highlighted gold/silver/bronze; the viewer's own row
// is always pinned visible if they're outside the top 10.

import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Crown, Medal, Trophy, Minus } from 'lucide-react';
import {
  getTeamLeaderboard,
  type LeaderboardEntry,
} from '@shared/services/teams';

interface TeamLeaderboardProps {
  teamId: string;
  /** The viewer's own uid, used to pin their row when outside the top N. */
  viewerUid: string;
  topN?: number;
}

export function TeamLeaderboard({ teamId, viewerUid, topN = 10 }: TeamLeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [cohortAverage, setCohortAverage] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getTeamLeaderboard(teamId)
      .then((data) => {
        if (!active) return;
        setEntries(data.entries);
        setCohortAverage(data.cohortAverage);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
      });
    return () => { active = false; };
  }, [teamId]);

  if (error) {
    return (
      <div className="rounded-xl border border-error/20 bg-error/5 p-4 text-sm font-body text-error">
        {error}
      </div>
    );
  }
  if (!entries) {
    return (
      <div className="rounded-xl border border-dark-border bg-dark-surface p-6 text-sm font-body text-content-muted">
        Loading leaderboard…
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dark-border bg-dark-surface p-6 text-sm font-body text-content-muted">
        No activity this week yet. Earn XP by completing nodes, checkpoints or recalibrations.
      </div>
    );
  }

  const top = entries.slice(0, topN);
  const viewerInTop = top.some((e) => e.uid === viewerUid);
  const viewer = !viewerInTop ? entries.find((e) => e.uid === viewerUid) ?? null : null;

  return (
    <div className="rounded-xl border border-dark-border bg-dark-surface overflow-hidden">
      <div className="px-4 py-3 border-b border-dark-border flex items-baseline justify-between">
        <div>
          <h3 className="font-heading text-base font-bold text-content">This week</h3>
          <p className="text-xs font-body text-content-muted">Resets Monday.</p>
        </div>
        <div className="text-right">
          <div className="text-xs font-body text-content-muted uppercase tracking-wider">Cohort avg</div>
          <div className="font-heading text-base font-bold text-gold-500">{cohortAverage.toLocaleString()} XP</div>
        </div>
      </div>
      <ul>
        {top.map((entry) => (
          <Row key={entry.uid} entry={entry} isViewer={entry.uid === viewerUid} />
        ))}
        {viewer && (
          <>
            <li className="px-4 py-2 text-xs font-body text-content-muted text-center border-t border-dark-border">
              … {entries.length - top.length} more
            </li>
            <Row entry={viewer} isViewer pinned />
          </>
        )}
      </ul>
    </div>
  );
}

function Row({
  entry, isViewer, pinned = false,
}: { entry: LeaderboardEntry; isViewer: boolean; pinned?: boolean }) {
  const podium = entry.rank === 1 ? 'gold' : entry.rank === 2 ? 'silver' : entry.rank === 3 ? 'bronze' : null;
  return (
    <li
      className={`flex items-center gap-3 px-4 py-3 border-t border-dark-border ${
        isViewer ? 'bg-dark-subtle' : ''
      } ${pinned ? 'border-t-2 border-t-indigo-400/30' : ''}`}
    >
      <RankBadge rank={entry.rank} podium={podium} />
      <Avatar entry={entry} />
      <div className="flex-1 min-w-0">
        <div className="font-body text-sm font-semibold text-content truncate">
          {entry.displayName ?? `Member ${entry.uid.slice(0, 6)}`}
          {isViewer && <span className="ml-2 text-xs text-indigo-400">(you)</span>}
        </div>
        <div className="text-xs font-body text-content-muted">
          {entry.currentStreakDays > 0 && `${entry.currentStreakDays}-day streak · `}
          {entry.totalXp.toLocaleString()} XP all-time
        </div>
      </div>
      <div className="text-right">
        <div className="font-heading text-sm font-bold text-content">
          {entry.weeklyXp.toLocaleString()} <span className="text-xs text-content-muted font-body">XP</span>
        </div>
        <RankChange change={entry.rankChange} />
      </div>
    </li>
  );
}

function RankBadge({ rank, podium }: { rank: number; podium: 'gold' | 'silver' | 'bronze' | null }) {
  const colour = podium === 'gold' ? '#F0A500' : podium === 'silver' ? '#C0C5D5' : podium === 'bronze' ? '#B97A56' : null;
  if (!colour) {
    return (
      <span className="shrink-0 w-7 text-center font-heading text-sm font-bold text-content-muted">
        {rank}
      </span>
    );
  }
  const Icon = rank === 1 ? Crown : rank === 2 ? Trophy : Medal;
  return (
    <span className="shrink-0 w-7 flex items-center justify-center" aria-label={`Rank ${rank}`}>
      <Icon size={18} style={{ color: colour }} />
    </span>
  );
}

function Avatar({ entry }: { entry: LeaderboardEntry }) {
  const [errored, setErrored] = useState(false);
  const src = entry.photoURL && !errored ? entry.photoURL : '/fingerprint-logo-transparent.png';
  return (
    <img
      src={src}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setErrored(true)}
      className="w-8 h-8 rounded-full object-cover border border-dark-border bg-dark-surface shrink-0"
    />
  );
}

function RankChange({ change }: { change: number }) {
  if (change === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-body text-content-muted">
        <Minus size={12} />
      </span>
    );
  }
  if (change > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-body text-success">
        <ArrowUp size={12} />
        {change}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-body text-error">
      <ArrowDown size={12} />
      {Math.abs(change)}
    </span>
  );
}
